import React, { SyntheticEvent, useEffect, useRef, useState } from 'react'
import { ScrollArrow } from './ScrollArrow'
import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  forwardRefWithCitrusTheme,
  JustifyContent,
  Overflow,
} from '@gousto-internal/citrus-react'

import { Recipe, RecipeUUID } from '@library/menu-service'
import { RecipeClickableImage } from './components/Recipe/RecipeClickableImage'
import { getMoodImage } from './utils/formatters'
import { ViewBasketButton } from './components/ViewBasketButton/ViewBasketButton'
import { PlaceholderBanner } from './components/PlaceholderBanner/PlaceholderBanner'
import { Tooltip } from './components/Tooltip/Tooltip'

const ScrollableRecipesContainer = forwardRefWithCitrusTheme(Box, (theme) => ({
  [`@media (min-width: ${theme.tokens.breakpoints[0]})`]: {
    '&[data-show-education-placeholder]': {
      flexDirection: 'row-reverse',
      gap: theme.spacing(4),
    },
  },
}))

export type ScrollableBasketRecipe = {
  recipe: Recipe
  numPortions?: number
}

const getRecipePortionLabel = (
  recipeNumPortions: number | null | undefined,
  basketNumPortions: number | null | undefined,
): string | null => {
  const portions = Number(recipeNumPortions)
  const basketPortions = Number(basketNumPortions)

  if (!portions || !basketPortions || portions === basketPortions) {
    return null
  }

  return `${portions} portion${portions === 1 ? '' : 's'}`
}

type ScrollableBasketProps = {
  basketLabelHTML?: string
  basketNumPortions?: number
  isCheckoutDisabled?: boolean
  isLoading?: boolean
  onClickViewBasket: (e: SyntheticEvent) => void
  onRecipeClick: (recipeUuid: RecipeUUID) => void
  selectedRecipes: ScrollableBasketRecipe[]
  showTooltip: boolean
  onCloseTooltip: () => void
}

export const ScrollableBasket = ({
  basketLabelHTML,
  basketNumPortions,
  isCheckoutDisabled = true,
  isLoading = true,
  onClickViewBasket,
  onRecipeClick,
  selectedRecipes,
  showTooltip,
  onCloseTooltip,
}: ScrollableBasketProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [hasOverflow, setHasOverflow] = useState(false)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)
  const [isAutoScrolling, setIsAutoScrolling] = useState(false)
  const showEducationPlaceholder = selectedRecipes.length === 1 && !showTooltip

  // Helper to update scroll position state
  const updateScrollPosition = () => {
    const scrollEl = scrollRef.current
    if (!scrollEl) return

    setAtStart(scrollEl.scrollLeft <= 1)
    setAtEnd(scrollEl.scrollLeft + scrollEl.offsetWidth >= scrollEl.scrollWidth - 1)
  }

  useEffect(() => {
    const scrollEl = scrollRef.current
    const containerEl = containerRef.current
    if (!scrollEl || !containerEl) return

    const checkOverflowAndScroll = () => {
      const scrollWidth = scrollEl.scrollWidth
      const containerWidth = containerEl.offsetWidth
      const screenWidth = window.innerWidth
      const visibleWidth = Math.min(containerWidth, screenWidth)

      setHasOverflow(scrollWidth > visibleWidth)
      updateScrollPosition()
    }

    checkOverflowAndScroll()
    window.addEventListener('resize', checkOverflowAndScroll)

    return () => window.removeEventListener('resize', checkOverflowAndScroll)
  }, [selectedRecipes])

  useEffect(() => {
    const scrollEl = scrollRef.current
    if (!scrollEl) return

    // Scroll the last recipe (newest) into view
    if (selectedRecipes.length > 0) {
      setIsAutoScrolling(true)
      // Smooth scroll to the end, accounting for right padding
      const targetLeft = scrollEl.scrollWidth - scrollEl.offsetWidth + 16
      if (typeof scrollEl.scrollTo === 'function') {
        scrollEl.scrollTo({ left: targetLeft, behavior: 'smooth' })
      } else {
        scrollEl.scrollLeft = targetLeft
      }
      setTimeout(() => {
        updateScrollPosition()
        setIsAutoScrolling(false)
      }, 350)
    } else {
      updateScrollPosition()
      setIsAutoScrolling(false)
    }
  }, [selectedRecipes, hasOverflow])

  // Track scroll position to show/hide arrows
  useEffect(() => {
    const scrollEl = scrollRef.current
    if (!scrollEl) return

    updateScrollPosition()
    scrollEl.addEventListener('scroll', updateScrollPosition)

    return () => scrollEl.removeEventListener('scroll', updateScrollPosition)
  }, [selectedRecipes, hasOverflow])

  const handleOnRecipeClick = (e: SyntheticEvent, recipeUuid: RecipeUUID) => {
    e.stopPropagation()
    onRecipeClick(recipeUuid)
  }

  // Scroll one recipe card at a time
  const scrollByCard = (direction: 'left' | 'right') => {
    const scrollEl = scrollRef.current
    if (!scrollEl) return

    const firstChild = scrollEl.firstElementChild as HTMLElement
    const scrollAmount = firstChild ? firstChild.offsetWidth + 16 /* gap */ : 200
    const scrollBy = direction === 'left' ? -scrollAmount : scrollAmount

    scrollEl.scrollBy({ left: scrollBy, behavior: 'smooth' })
  }

  return (
    <Box
      data-testid="container"
      display={Display.Flex}
      flexDirection={[FlexDirection.Column, FlexDirection.Row]}
      width="100%"
      justifyContent={JustifyContent.FlexEnd}
      alignItems={AlignItems.Center}
      wrap={false}
      paddingV={2}
      paddingH={[2, 5]}
      gap={2}
      bg={Color.NeutralGrey_50}
    >
      {selectedRecipes.length === 1 && (
        <Tooltip open={showTooltip} onClose={() => onCloseTooltip()} />
      )}
      <Box
        position={DisplayPosition.Relative}
        ref={containerRef}
        flexGrow={1}
        minWidth="0"
        maxWidth="100vw"
        width="100%"
      >
        {hasOverflow && !atStart && (
          <ScrollArrow direction="left" onClick={() => scrollByCard('left')} />
        )}

        <Box
          display={Display.Flex}
          justifyContent={[JustifyContent.FlexStart, JustifyContent.FlexEnd]}
        >
          <ScrollableRecipesContainer
            ref={scrollRef}
            display={Display.Flex}
            gap={2}
            data-show-education-placeholder={showEducationPlaceholder ? '' : undefined}
            data-testid="scrollable-recipes"
            overflowX={Overflow.Scroll}
            paddingH={2}
            width={['100%', 'auto']}
            style={{
              scrollBehavior: 'smooth',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              width: showEducationPlaceholder ? '100%' : undefined,
            }}
          >
            {selectedRecipes.map(({ recipe, numPortions }, index) => (
              <Box key={`${recipe.id}-${index}`} flexShrink={0} width={['71px', '62px']}>
                {recipe && !isLoading && (
                  <RecipeClickableImage
                    image={getMoodImage(recipe.images)}
                    onClick={(e) => handleOnRecipeClick(e, recipe.id)}
                    name={recipe.name}
                    portionLabel={getRecipePortionLabel(numPortions, basketNumPortions)}
                  />
                )}
              </Box>
            ))}
            {showEducationPlaceholder && <PlaceholderBanner />}
          </ScrollableRecipesContainer>
        </Box>

        {hasOverflow && !atEnd && !isAutoScrolling && (
          <ScrollArrow direction="right" onClick={() => scrollByCard('right')} />
        )}
      </Box>

      <Box data-testid="viewBasketButton" flexShrink={0} width={['100%', 'auto']}>
        <ViewBasketButton
          labelHTML={basketLabelHTML}
          disabled={isCheckoutDisabled}
          onClick={onClickViewBasket}
          counter={selectedRecipes.length}
        />
      </Box>
    </Box>
  )
}
